import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormGroup } from '@angular/forms';
import swal from 'sweetalert2';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '@app/shared/auth/auth.service';
import { SystemService } from '@app/core/common/services/system.service';
import { NoWhitespaceValidator } from '@app/validators/no-whitespace-validator.validator';
import { ENV, LOCAL_DEFAULT_PAGE, PROD_DEFAULT_PAGE, STAGING_DEFAULT_PAGE } from '@env/environment.constant';
import { BASIC_MONTHLY,  ENTERPRISE_MONTHLY } from '@app/shared/constants/SubPlanConstant';
import { BASIC_ACTIVATION_AMOUNT, ENTERPRISE_ACTIVATION_AMOUNT } from '@app/shared/constants/ActivationAmountConstant';
import { XenditFeeService } from '@app/shared/services/xendit-fee.service';
import { CurrencyPipe } from '@angular/common';
import { PhoneNumberValidator } from '@app/validators/phone-number-validator.validator';
import { SplitService } from '@app/shared/services/helpers/split.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-signup',
  providers: [CurrencyPipe],
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})
export class SignupComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  formData: UntypedFormGroup;
  submitted: boolean = false;
  readonly PAGE_SUBSCRIPTION = 3;
  readonly PAGE_PERSONAL_INFO = 1;
  readonly PAGE_BILLING_INFO = 2;
  BASIC = BASIC_MONTHLY;
  ENTERPRISE = ENTERPRISE_MONTHLY;
  BASIC_ACTIVATION_AMOUNT = BASIC_ACTIVATION_AMOUNT;
  domainHostName: string;

  subscriptionPlans: any[] = [];
  page: number = 1;
  provinces: any = {};
  provinceList: any[] = [];

  // Modal states
  showEmailVerificationModal: boolean = false;
  showPaymentModal: boolean = false;

  // Email verification
  verificationForm: UntypedFormGroup;
  verificationSubmitted: boolean = false;
  registrationId: number | null = null;
  userEmail: string = '';
  resendCooldown: number = 0;
  resendTimer: any;

  // Signup token handling
  signupToken: string | null = null;
  retainedData: any = null;
  isLoadingRetainedData: boolean = false;

  // Expired/invalid signup link recovery
  showDeadLinkRecovery: boolean = false;
  deadLinkMessage: string = '';
  isRequestingNewLink: boolean = false;

  // Add loading state for payment
  isProcessingPayment: boolean = false;

  // Payment gateway selection driven by Split.io `xendit_feature` flag
  selectedGateway: 'hitpay' | 'xendit' = 'hitpay';

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private systemSvc: SystemService,
    private currencyPipe: CurrencyPipe,
    private splitService: SplitService,
    private xenditFeeService: XenditFeeService,
) {}

  ngOnInit() {
    this.xenditFeeService.reload();
    // Resolve which payment gateway to use (mirrors pay-hitpay-form.component)
    this.splitService.getTreatmentObservable('xendit_feature')
      .pipe(takeUntil(this.destroy$))
      .subscribe((treatment) => {
        this.selectedGateway = treatment === 'on' ? 'xendit' : 'hitpay';
      });

    // Check for signup token in URL parameters
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const token = params['reference'];
      const email = params['email'];

      if (token) {
        this.signupToken = token;
        this.userEmail = email || '';
        this.loadRetainedData(token);
      }
    });

    this.auth.getPlans().pipe(takeUntil(this.destroy$)).subscribe((resp: any) => {
        if (resp) {
          resp.forEach((plan) => {
            this.subscriptionPlans.push({
              id: plan.id,
              name: this.getName(plan.name),
              desc: this.getName(plan.descriptions),
              price: plan.price,
              interval: plan.invoice_interval,
              period: plan.invoice_period
            });
          });
        }
      });
    this.systemSvc.getPlaces().pipe(takeUntil(this.destroy$)).subscribe(
        (regions: any) => {
            Object.keys(regions).forEach((region) => {
                const provinces = Object.keys(regions[region].province_list);
                provinces.forEach((province) => {
                    const cities = regions[region].province_list[province].municipality_list;
                    this.provinces[province] = Object.keys(cities);
                    this.provinceList.push(province);
                })
            })
        }
    )

    this.formData = this.fb.group({
        personalInfo: this.fb.group({
            firstname: ['', [Validators.required, NoWhitespaceValidator()]],
            lastname: ['', [Validators.required, NoWhitespaceValidator()]],
            email: ['', [Validators.required, Validators.email]],
            mobile: ['', [Validators.required, NoWhitespaceValidator()]],
            licenseNo: ['', [Validators.required, NoWhitespaceValidator()]],
            ptrNo: ['', [Validators.required, NoWhitespaceValidator()]],
        }),
        clinicDetails: this.fb.group({
            clinicName: ['', [Validators.required, NoWhitespaceValidator()]],
            clinicSpecialization: ['General Dentistry', [Validators.required, NoWhitespaceValidator()]],
            clinicContact: ['', [Validators.required, NoWhitespaceValidator()]],
            clinicAddress: ['', [Validators.required, NoWhitespaceValidator()]],
        }),
        billingInfo: this.fb.group({
            billingName: ['',  [Validators.required, NoWhitespaceValidator()]],
            billingEmail: ['',  [Validators.required, Validators.email]],
            billingPhone: ['',  [Validators.required, PhoneNumberValidator()]],
            billingCity: ['', [Validators.required, NoWhitespaceValidator()]],
            billingZip: ['', [Validators.required, NoWhitespaceValidator()]],
            billingProvince: ['', [Validators.required, NoWhitespaceValidator()]],
            billingAddress: ['', [Validators.required, NoWhitespaceValidator()]],
        }),
        planId: [this.BASIC],
    });

    // Initialize verification form
    this.verificationForm = this.fb.group({
      verification_code: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });

  }

  next() {
    this.page++;
  }

  previousPage(){
    this.page--;
  }
  getName(text) {
    let name: any = '';
    try {
      const strr = JSON.parse(text);
      name = strr.en;
    } catch (e) {
      name = text;
    }
    return name;
  }

  onSubmit() {
    if (this.submitted) {
      return;
    }
    if (this.formData.invalid) {
      swal('', 'Please fill all fields correctly.', 'error');
      return;
    }
      // Re-enable disabled fields before submitting
    if (this.signupToken) {
      this.personalInfo.get('email')?.enable();
      this.billingInfo.get('billingEmail')?.enable();
    }
    if (!this.formData.value.planId || this.formData.value.planId <= 0) {
      // swal('', 'Please select the schedule you want to pay your subscription.', 'error');
      swal('', 'Error. Please refresh the page and try again.', 'error');
      return;
    }
    const amount =  BASIC_ACTIVATION_AMOUNT;
    const purpose = this.personalInfo.value.firstname + ' ' + this.personalInfo.value.lastname + ': Basic Plan'  + ' Activation/Re-activation Fee';
    let fd = this.formData.value;
    const defaultPage: string = `${this.getDomainHostName()}/auth/login`;

    fd = {
        ...fd.personalInfo,
        ...fd.billingInfo,
        clinicDetails: JSON.stringify(fd.clinicDetails),
        planId: fd.planId,
        subDomain: fd.subDomain,
        redirectUrl: defaultPage,
        purpose: purpose,
        amount: amount,
      };
    this.submitted = true;

    this.auth.register(fd).subscribe(
      (resp: any) => {
        if (resp.email_verification_required) {
          // Show email verification modal
          this.registrationId = resp.registration_id;
          this.userEmail = fd.email;
          this.showEmailVerificationModal = true;
          this.submitted = false;
        } else if (resp.email_already_verified) {
          // Email already verified - show payment modal directly
          this.registrationId = resp.registration_id;
          this.userEmail = fd.email;
          this.showPaymentModal = true;
          this.submitted = false;
        } else if (resp.url) {
          // Legacy flow - direct payment
          window.location.href = resp.url.toString();
        }
      },
      (error) => {
        this.submitted = false;
        const serverMessage = this.extractServerError(error);
        if (serverMessage) {
          swal('Registration Error.', serverMessage, 'warning');
          return;
        }
        const emailRef = fd.email || 'not provided';
        swal(
          'Registration Error.',
          `Something went wrong while submitting your registration. Please try again, or contact support at 0917 705 7827 / 0917.770.9504.`,
          'warning',
        );
      },
    );
  }


  private extractServerError(error: any): string | null {
    const body = error?.error;
    if (!body) {
      return null;
    }
    if (typeof body === 'string' && body.trim().length > 0) {
      return body;
    }
    if (typeof body.message === 'string' && body.message.trim().length > 0) {
      return body.message;
    }
    if (typeof body.error === 'string' && body.error.trim().length > 0) {
      return body.error;
    }
    if (body.errors && typeof body.errors === 'object') {
      const firstField = Object.keys(body.errors)[0];
      const firstMessage = firstField ? body.errors[firstField] : null;
      if (Array.isArray(firstMessage) && firstMessage.length > 0) {
        return firstMessage[0];
      }
      if (typeof firstMessage === 'string') {
        return firstMessage;
      }
    }
    return null;
  }

  // Email verification methods
  onVerifyEmail() {
    if (this.verificationSubmitted) {
      return;
    }

    if (this.verificationForm.invalid) {
      swal('', 'Please enter a valid 6-digit verification code.', 'error');
      return;
    }

    this.verificationSubmitted = true;
    const verificationCode = this.verificationForm.value.verification_code;

    this.auth.verifyEmailCode(this.userEmail, verificationCode).subscribe(
      (resp: any) => {
        if (resp.success) {
          this.showEmailVerificationModal = false;
          this.registrationId = resp.data.registration_id;
          this.showPaymentModal = true;
          this.verificationSubmitted = false;
        } else {
          this.verificationSubmitted = false;
          swal('Verification Failed', resp.message || 'Invalid verification code. Please try again.', 'error');
        }
      },
      (error) => {
        this.verificationSubmitted = false;
        const errorMessage = error.error?.message || 'An error occurred while verifying your email.';
        swal('Verification Error', errorMessage, 'error');
      }
    );
  }

  resendVerificationCode() {
    if (this.resendCooldown > 0) {
      return;
    }

    this.auth.resendVerificationCode(this.userEmail).subscribe(
      (resp: any) => {
        if (resp.success) {
          swal('Code Sent', 'A new verification code has been sent to your email.', 'success');
          this.startResendCooldown();
        } else {
          swal('Error', resp.message || 'Failed to resend verification code.', 'error');
        }
      },
      (error) => {
        const errorMessage = error.error?.message || 'Failed to resend verification code.';
        swal('Error', errorMessage, 'error');
      }
    );
  }

  private startResendCooldown() {
    this.resendCooldown = 60; // 60 seconds cooldown
    this.resendTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.resendTimer);
      }
    }, 1000);
  }

  // Load retained data from token
  loadRetainedData(token: string) {
    this.isLoadingRetainedData = true;

    this.auth.getRetainedData(token).subscribe(
      (resp: any) => {
        this.isLoadingRetainedData = false;
        if(resp){
          this.retainedData = resp;
          this.registrationId = resp.registration_id;
          this.populateFormWithRetainedData(resp);
          swal('Data Loaded', 'Your registration data has been loaded successfully.', 'success');
        } else {
          this.handleDeadSignupLink('Your signup link has expired or is invalid.');
        }
      },
      (error) => {
        this.isLoadingRetainedData = false;
        const errorMessage = error.error?.message || 'Failed to load retained data.';
        this.handleDeadSignupLink(errorMessage);
      }
    );
  }

  /**
   * Handle an expired/invalid signup link.
   *
   * Clearing signupToken is the important part: the component is NOT destroyed by
   * the redirect below (same route, only query params change), so a stale token
   * would survive and keep routing proceedWithPayment() down the token path,
   * failing forever until a hard page reload.
   */
  private handleDeadSignupLink(message: string) {
    this.signupToken = null;
    this.retainedData = null;
    this.registrationId = null;

    // Offer a one-click re-send when we know who they are, instead of a dead end.
    if (this.userEmail) {
      this.deadLinkMessage = message;
      this.showDeadLinkRecovery = true;
      this.router.navigate(['/auth/signup'], { queryParams: {} });
      return;
    }

    swal('Signup Link Expired', message + ' You will be redirected to start fresh.', 'error').then(() => {
      this.router.navigate(['/auth/signup'], { queryParams: {} });
    });
  }

  /**
   * Ask the backend to email a fresh signup link.
   *
   * For an already-verified registration the API mints a new token and re-sends
   * the link, so the subscriber never has to re-enter the whole form.
   */
  requestNewSignupLink() {
    if (!this.userEmail || this.isRequestingNewLink) {
      return;
    }

    this.isRequestingNewLink = true;

    this.auth.sendVerificationCode(this.userEmail).subscribe(
      (resp: any) => {
        this.isRequestingNewLink = false;
        if (resp && resp.success !== false) {
          this.showDeadLinkRecovery = false;
          swal(
            'Link Sent',
            'We have emailed you a fresh signup link. Please check your inbox (and your spam folder).',
            'success'
          );
        } else {
          swal('Error', (resp && resp.message) || 'Failed to send a new signup link.', 'error');
        }
      },
      (error) => {
        this.isRequestingNewLink = false;
        const errorMessage = error.error?.message || 'Failed to send a new signup link.';
        swal('Error', errorMessage, 'error');
      }
    );
  }

  dismissDeadLinkRecovery() {
    this.showDeadLinkRecovery = false;
  }

  // Populate form with retained data
  populateFormWithRetainedData(data: any) {
    // Parse clinic details if it's a string
    let clinicDetails = data.clinicDetails;
    if (typeof clinicDetails === 'string') {
      try {
        clinicDetails = JSON.parse(clinicDetails);
      } catch (e) {
        clinicDetails = {};
      }
    }

    this.formData.patchValue({
      personalInfo: {
        firstname: data.firstName || '',      // Fixed: was data.first_name
        lastname: data.lastName || '',        // Fixed: was data.last_name
        email: data.email || '',
        mobile: data.mobile || '',
        licenseNo: data.licenseNo || '',
        ptrNo: data.ptrNo || ''
      },
      clinicDetails: {
        clinicName: clinicDetails.clinicName || '',           // Fixed: was clinic_name
        clinicSpecialization: clinicDetails.clinicSpecialization || 'General Dentistry', // Fixed: was clinic_specialization
        clinicContact: clinicDetails.clinicContact || '',     // Fixed: was clinic_contact
        clinicAddress: clinicDetails.clinicAddress || ''      // Fixed: was clinic_address
      },
      billingInfo: {
        billingName: data.billingName || '',
        billingEmail: data.billingEmail || '',
        billingPhone: data.billingPhone || '',
        billingCity: data.billingCity || '',
        billingZip: data.billingZip || '',
        billingProvince: data.billingProvince || '',
        billingAddress: data.billingAddress || ''
      },
      planId: data.plan_id || this.BASIC
    });
    this.personalInfo.get('email')?.disable();
    this.billingInfo.get('billingEmail')?.disable();
  }

  // Payment methods
  proceedWithPayment() {
    // Prevent multiple clicks
    if (this.isProcessingPayment) {
      return;
    }

    // Check if we have a signup token
    if (this.signupToken) {
      this.proceedWithTokenPayment();
      return;
    }

    // Regular payment flow
    if (!this.registrationId) {
      swal('Error', 'Registration ID not found. Please try registering again.', 'error');
      return;
    }

    this.isProcessingPayment = true;

    this.auth.proceedWithPayment(this.registrationId, this.selectedGateway).subscribe(
      (resp: any) => {
        this.isProcessingPayment = false;
        if (resp.url) {
          // Redirect to payment gateway
          window.location.href = resp.url.toString();
        } else {
          swal('Error', 'Failed to proceed with payment. Please contact support.', 'error');
        }
      },
      (error) => {
        this.isProcessingPayment = false;
        const errorMessage = error.error?.message || 'Failed to proceed with payment.';
        swal('Payment Error', errorMessage, 'error');
      }
    );
  }

  // Proceed with payment using token
  proceedWithTokenPayment() {
    if (!this.signupToken) {
      swal('Error', 'Signup token not found. Please try again.', 'error');
      return;
    }

    this.isProcessingPayment = true;

    this.auth.proceedPaymentWithToken(this.signupToken, this.selectedGateway).subscribe(
      (resp: any) => {
        this.isProcessingPayment = false;
        if(resp && resp.url){
          window.location.href = resp.url.toString();
        } else {
          swal('Error', resp.message || 'Failed to proceed with payment. Please contact support.', 'error');
        }
      },
      (error) => {
        this.isProcessingPayment = false;
        const errorMessage = error.error?.message || 'Failed to proceed with payment.';
        swal('Payment Error', errorMessage, 'error');
      }
    );
  }

  closeEmailVerificationModal() {
    this.showEmailVerificationModal = false;
  }



  get billingInfo(): FormGroup {
    return this.formData.get('billingInfo') as FormGroup;
  }
  get personalInfo(): FormGroup {
    return this.formData.get('personalInfo') as FormGroup;
  }

  get clinicDetails(): FormGroup {
    return this.formData.get('clinicDetails') as FormGroup;
  }

  get xenditFee(): number {
    return this.xenditFeeService.calculate(BASIC_ACTIVATION_AMOUNT);
  }

  get xenditFeeEnabled(): boolean {
    return this.xenditFeeService.hasFee();
  }

  get xenditFeeLabel(): string {
    return this.xenditFeeService.label();
  }

  backToLogin() {
    this.router.navigate(['login'], { relativeTo: this.route.parent });
  }
  getDomainHostName(){
    const domain = window.location.hostname;
    if (domain.includes('localhost') || domain.includes('127.0.0.1')) {
        return LOCAL_DEFAULT_PAGE;
    }

    // Check for staging environment
    if (domain.includes('staging.')) {
    return STAGING_DEFAULT_PAGE;
    }
    return PROD_DEFAULT_PAGE
  }

  ngOnDestroy() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
